import { useRef } from 'react'

import { TooltipPortalContext } from '@components/shelfDetails/TooltipPortalContext'
import { cx } from '@utils/cx.ts'

import type { CSSProperties, PointerEvent, ReactNode } from 'react'

interface ShelfContainerProps {
  aspectRatio: number
  children: ReactNode
  className?: string
  onPointerMove?: (e: PointerEvent) => void
  onPointerLeave?: () => void
}

/**
 * Responsive container for shelf visualizations that maintains aspect ratio
 * while preventing overflow in both expanded and docked modes.
 *
 * The container automatically adjusts to fit within available space:
 * - Landscape shelves (width > height): fills width, adjusts height
 * - Portrait shelves (height > width): fills height, adjusts width
 */
function ShelfContainer({ aspectRatio, children, className = '', onPointerMove, onPointerLeave }: ShelfContainerProps) {
  const portalContainerRef = useRef<HTMLDivElement>(null)

  const containerStyle: CSSProperties = {
    aspectRatio,
    maxWidth: '100%',
    maxHeight: '100%',
    width: aspectRatio > 1 ? '100%' : 'auto',
    height: aspectRatio > 1 ? 'auto' : '100%',
  }

  return (
    <div
      ref={portalContainerRef}
      className={cx('relative flex size-full items-center justify-center', className)}
      data-testid="shelf-container"
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      <TooltipPortalContext.Provider value={portalContainerRef}>
        <div className='relative size-full' style={containerStyle}>
          {children}
        </div>
      </TooltipPortalContext.Provider>
    </div>
  )
}

export default ShelfContainer

